import { config } from '@/config';

/**
 * An error whose message was written for end users, so it is still shown in production.
 * Throw this (instead of a plain `Error`) for text such as "Please select a provider".
 */
export class UserFacingError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'UserFacingError';
  }
}

/** Stripe error types whose message is written for the card holder (e.g. "Your card was declined."). */
const STRIPE_USER_FACING_TYPES = new Set(['card_error', 'validation_error']);

function readMessage(err: unknown): string | undefined {
  if (typeof err === 'string') return err;
  if (err !== null && typeof err === 'object' && 'message' in err) {
    const { message } = err as { message: unknown };
    if (typeof message === 'string') return message;
  }
  return undefined;
}

function isAllowlisted(err: unknown): boolean {
  if (err instanceof UserFacingError) return true;
  if (err !== null && typeof err === 'object' && 'type' in err) {
    const { type } = err as { type: unknown };
    return typeof type === 'string' && STRIPE_USER_FACING_TYPES.has(type);
  }
  return false;
}

/**
 * Text from a caught error that is safe to show a user.
 *
 * Outside production the full message is returned so engineers can debug. In production only a
 * `UserFacingError` or a Stripe `card_error` / `validation_error` is returned; every other error
 * yields `fallback` and the original is logged to the browser console.
 */
export function getUserFacingErrorMessage(err: unknown, fallback = ''): string {
  const message = readMessage(err);
  if (!config.isProduction) return message || fallback;
  if (isAllowlisted(err) && message) return message;
  console.error(err);
  return fallback;
}
